import * as THREE from 'three';
import EventEmitter from '../Utils/EventEmitter';

/**
 * Scene class
 * Manages the Three.js scene
 */
export class Scene extends EventEmitter {
  // Static instance property
  static instance = null;

  /**
   * Get the singleton instance of the class
   * @returns {Scene} The singleton instance
   */
  static getInstance() {
    if (!Scene.instance) {
      Scene.instance = new Scene();
    }
    return Scene.instance;
  }

  /**
   * Constructor
   */
  constructor() {
    // Ensure this is a singleton
    if (Scene.instance) {
      return Scene.instance;
    }

    super();
    this.instance = new THREE.Scene();

    // Set instance
    Scene.instance = this;
  }

  /**
   * Add an object to the scene
   * @param {Object} object - THREE.js object to add
   */
  add(object) {
    this.instance.add(object);
    this.trigger('objectAdded', [object]);
  }

  /**
   * Remove an object from the scene
   * @param {Object} object - THREE.js object to remove
   */
  remove(object) {
    this.instance.remove(object);
    this.trigger('objectRemoved', [object]);
  }

  /**
   * Set background color
   * @param {String|Number} color - Color value
   */
  setBackgroundColor(color) {
    this.instance.background = new THREE.Color(color);
    this.trigger('backgroundChanged', [color]);
  }

  /**
   * Clear the scene
   */
  clear() {
    // Remove all objects
    while(this.instance.children.length > 0) {
      const object = this.instance.children[0];

      // Dispose geometries and materials
      if (object.geometry) object.geometry.dispose();

      if (object.material) {
        if (Array.isArray(object.material)) {
          object.material.forEach(material => material.dispose());
        } else {
          object.material.dispose();
        }
      }

      this.instance.remove(object);
    }

    this.trigger('cleared');
  }

  /**
   * Find object by name
   * @param {String} name - Object name
   * @returns {Object|null} - Found object or null
   */
  findObject(name) {
    return this.instance.getObjectByName(name);
  }

  /**
   * Traverse all objects in scene
   * @param {Function} callback - Callback for each object
   */
  traverse(callback) {
    this.instance.traverse(callback);
  }
}
